/**
 * Folder preview — what the preview pane shows when a single folder is selected.
 *
 * A folder has nothing to read, so the pane shows its identity (icon + name) and
 * the one fact worth knowing at a glance — how many things are directly inside
 * it and when it last changed. This is the acknowledgment a single-click on a
 * folder owes the user: without it the click looks like it did nothing, which is
 * exactly the "I can't tell if it worked" confusion the Finder redesign is after.
 *
 * Double-click still navigates into the folder (handled by the row); this pane
 * is the single-click feedback, not a substitute for opening it.
 */

import type { ReactElement } from 'react';
import { type VfsFileEntry } from '../shared/api-client';
import { FileIconComponent } from './FileIconComponent';
import './FolderPreviewComponent.css';

export interface FolderPreviewComponentProps {
  entry: VfsFileEntry;
}

function formatModified(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], { month: 'short', 'day': 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function FolderPreviewComponent({ entry }: FolderPreviewComponentProps): ReactElement {
  const count = entry.sizeBytes; // directoryEntriesFor puts the direct-item count here
  const itemsLabel = count === 1 ? '1 item' : count > 1 ? `${count} items` : 'Empty folder';
  return (
    <div className="folder-preview">
      <div className="folder-preview-head">
        <FileIconComponent kind="folder" size={36} />
        <div className="folder-preview-heading">
          <span className="folder-preview-name" title={entry.path}>{entry.name}</span>
          <span className="folder-preview-subtitle">Folder</span>
        </div>
      </div>
      <dl className="folder-preview-meta">
        <div className="folder-preview-meta-row">
          <dt>Contents</dt>
          <dd>{itemsLabel}</dd>
        </div>
        <div className="folder-preview-meta-row">
          <dt>Modified</dt>
          <dd>{formatModified(entry.updatedAt)}</dd>
        </div>
        <div className="folder-preview-meta-row">
          <dt>Path</dt>
          <dd className="folder-preview-meta-path" title={entry.path}>{entry.path}</dd>
        </div>
      </dl>
      <p className="folder-preview-hint">Double-click to open this folder.</p>
    </div>
  );
}
